import { FormEvent, ReactElement, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Field, Input, Label, Paragraph, ValidationMessage } from '@digdir/designsystemet-react';
import { AuthLayout } from '../../components/AuthLayout';
import { MicrosoftSignInButton } from '../../components/MicrosoftSignInButton';
import { useLoginMutation } from '../../hooks';
import { useCheckSSOAvailabilityMutation } from '../../hooks/mutations/useCheckSSOAvailabilityMutation';
import { get } from '../../utils/networking';
import { Routes } from '../../enums/Routes.ts';
import { HRRolesUtils } from '../../utils/RolesUtils/HRRoles.utils.ts';
import { PartnerAdminRolesUtils } from '../../utils/RolesUtils/PartnerAdminRoles.utils.ts';
import { SafetyRepRolesUtils } from '../../utils/RolesUtils/SafetyRepRoles.utils.ts';
import { isEmail, loginError, LoginError } from './loginView';
import type { UserOnboarding } from '../../api/userOnboarding';
import { homeFor } from '../GettingStarted/gettingStartedView';
import { takeReturnPath } from '../../utils/returnPath';
import classes from './LoginPage.module.css';

type Step = 'email' | 'password' | 'sso';

export const LoginPage = (): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const loginMutation = useLoginMutation();
  const ssoMutation = useCheckSSOAvailabilityMutation();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState<'empty' | 'wrong' | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<LoginError | null>(null);
  const [redirectError, setRedirectError] = useState<string | null>(null);

  // Innlogging med Microsoft sender feil tilbake i adressen
  useEffect(() => {
    const message = new URLSearchParams(window.location.search).get('error');
    if (message) {
      setRedirectError(decodeURIComponent(message));
      window.history.replaceState({}, '', '/');
    }
  }, []);

  const focus = (id: string) => setTimeout(() => document.getElementById(id)?.focus(), 0);

  const submitEmail = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = String(new FormData(event.currentTarget).get('email') ?? '').trim();
    setEmail(value);
    if (!isEmail(value)) {
      setEmailError(true);
      focus('login-email');
      return;
    }
    setEmailError(false);
    setError(null);
    ssoMutation.mutate(value, {
      onSuccess: (data) => setStep(data?.ssoAvailable ? 'sso' : 'password'),
      onError: () => setStep('password'),
    });
  };

  const goToApp = async (roles: Parameters<typeof HRRolesUtils.isHRUser>[0]) => {
    // Appen må vite at brukeren er innlogget før vi navigerer, ellers sendes hen tilbake hit
    queryClient.setQueryData(['IsLoggedIn'], { isLoggedIn: true });
    // A link that needed the login, such as one from an email, opens first
    const back = takeReturnPath();
    if (back) return navigate(back);
    // A new account continues «Kom i gang» on the page of its goal, until every point is done
    try {
      const onboarding = await get<UserOnboarding>('/api/me/onboarding');
      const home = onboarding?.gettingStarted ? homeFor(onboarding.goal) : null;
      if (home) return navigate(home);
    } catch {
      // Without it, the user lands where their role does
    }
    if (HRRolesUtils.isHRUser(roles)) return navigate(Routes.HRDashboard);
    if (PartnerAdminRolesUtils.isPartnerAdminUser(roles)) return navigate(Routes.PartnerAdmin);
    if (SafetyRepRolesUtils.isOnlySafetyRep(roles)) return navigate(Routes.SafetyRepOverview);
    navigate(Routes.Dashboard);
  };

  const submitPassword = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const password = String(new FormData(event.currentTarget).get('password') ?? '');
    setError(null);
    if (!password) {
      setPasswordError('empty');
      focus('login-password');
      return;
    }
    setPasswordError(null);
    loginMutation.mutate(
      { email, password },
      {
        onSuccess: (response) => goToApp(response.userAssignedRoles),
        onError: (err) => {
          const kind = loginError(err);
          if (kind === 'wrongCredentials') {
            setPasswordError('wrong');
            focus('login-password');
          } else {
            setError(kind);
          }
        },
      },
    );
  };

  const changeEmail = () => {
    setStep('email');
    setPasswordError(null);
    setError(null);
    ssoMutation.reset();
    focus('login-email');
  };

  const titles: Record<Step, string> = {
    email: t('auth.login.title'),
    password: t('auth.login.passwordTitle'),
    sso: t('auth.login.ssoTitle'),
  };

  return (
    <AuthLayout
      title={titles[step]}
      lead={step === 'email' ? t('auth.login.lead') : t('auth.login.loggingInAs')}
      below={
        <Paragraph>
          {t('auth.login.noAccount')} <Link to={`/${Routes.SignUp}`} className="ds-link">{t('auth.login.trial')}</Link>
        </Paragraph>
      }
    >
      {redirectError && (
        <Alert data-color="danger" role="alert">
          <Paragraph>{redirectError}</Paragraph>
        </Alert>
      )}
      {error && (
        <Alert data-color="danger" role="alert">
          <Paragraph>{t(`auth.login.errors.${error}`)}</Paragraph>
        </Alert>
      )}

      {step === 'email' ? (
        <form className={classes.form} onSubmit={submitEmail} noValidate>
          <Field>
            <Label htmlFor="login-email">{t('auth.login.email')}</Label>
            <Input
              id="login-email"
              name="email"
              type="email"
              inputMode="email"
              autoComplete="username"
              autoFocus
              defaultValue={email}
              aria-invalid={emailError}
              aria-describedby={emailError ? 'login-email-error' : undefined}
            />
            {emailError && <ValidationMessage id="login-email-error">{t('auth.errors.email')}</ValidationMessage>}
          </Field>
          <Button type="submit" className={classes.fullWidth} loading={ssoMutation.isPending}>
            {t('auth.login.next')}
          </Button>
        </form>
      ) : (
        <div className={classes.form}>
          <div className={classes.account}>
            <span className={classes.accountEmail}>
              <span aria-hidden="true" className={classes.initial}>
                {email[0]?.toUpperCase()}
              </span>
              <span className={classes.emailText}>{email}</span>
            </span>
            <Button variant="tertiary" data-size="sm" onClick={changeEmail} aria-label={t('auth.login.changeEmail')}>
              {t('auth.login.change')}
            </Button>
          </div>

          {step === 'sso' ? (
            // Organisasjonen og koblingen ligger i en signert informasjonskapsel på serveren
            <MicrosoftSignInButton onClick={() => (window.location.href = '/api/auth/sso/login')} />
          ) : (
            <form className={classes.form} onSubmit={submitPassword} noValidate>
              <input type="email" name="username" autoComplete="username" value={email} readOnly hidden />
              <Field>
                <Label htmlFor="login-password">{t('auth.login.password')}</Label>
                <div className={classes.passwordWrap}>
                  <Input
                    id="login-password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    autoFocus
                    aria-invalid={!!passwordError}
                    aria-describedby={passwordError ? 'login-password-error' : undefined}
                  />
                  <Button
                    variant="tertiary"
                    data-size="sm"
                    className={classes.showButton}
                    aria-pressed={showPassword}
                    aria-controls="login-password"
                    onClick={() => setShowPassword((shown) => !shown)}
                  >
                    {showPassword ? t('auth.login.hide') : t('auth.login.show')}
                  </Button>
                </div>
                {passwordError && (
                  <ValidationMessage id="login-password-error">
                    {t(passwordError === 'empty' ? 'auth.login.errors.password' : 'auth.login.errors.wrongCredentials')}
                  </ValidationMessage>
                )}
              </Field>
              <Button type="submit" className={classes.fullWidth} loading={loginMutation.isPending}>
                {t('auth.login.submit')}
              </Button>
              <Link to={`/${Routes.ForgotPassword}`} state={{ email }} className={`ds-link ${classes.center}`}>
                {t('auth.login.forgot')}
              </Link>
            </form>
          )}
        </div>
      )}
    </AuthLayout>
  );
};
